import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const ClientAuthContext = createContext(undefined);

export function ClientAuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [isLoading, setIsLoading] = useState(isSupabaseConfigured);
    const [lastError, setLastError] = useState(null);

    useEffect(() => {
        if (!isSupabaseConfigured || !supabase) {
            setIsLoading(false);
            return undefined;
        }

        let isMounted = true;

        supabase.auth
            .getSession()
            .then(({ data, error }) => {
                if (!isMounted) {
                    return;
                }
                if (error) {
                    console.warn("Client auth oturum okunamadı", error);
                    setLastError(error);
                }
                setSession(data?.session ?? null);
                setIsLoading(false);
            })
            .catch((error) => {
                if (isMounted) {
                    console.error("Client auth oturum alınamadı", error);
                    setLastError(error);
                    setIsLoading(false);
                }
            });

        const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession ?? null);
        });

        return () => {
            isMounted = false;
            listener?.subscription?.unsubscribe?.();
        };
    }, []);

    const guardSupabase = () => {
        if (!isSupabaseConfigured || !supabase) {
            throw new Error(
                "Supabase ortam değişkenleri tanımlı değil. Lütfen REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY değerlerini ayarlayın."
            );
        }
    };

    const signIn = async ({ email, password }) => {
        guardSupabase();
        setLastError(null);
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            setLastError(error);
            throw error;
        }
        setSession(data.session ?? null);
        return data;
    };

    const signUp = async ({ email, password, metadata }) => {
        guardSupabase();
        setLastError(null);
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: metadata ?? {},
                emailRedirectTo: window?.location?.origin ? `${window.location.origin}/client/login` : undefined,
            },
        });
        if (error) {
            setLastError(error);
            throw error;
        }
        return data;
    };

    const signOut = async () => {
        guardSupabase();
        const { error } = await supabase.auth.signOut();
        if (error) {
            setLastError(error);
            throw error;
        }
        setSession(null);
    };

    const value = useMemo(
        () => ({
            isSupabaseReady: isSupabaseConfigured && Boolean(supabase),
            isLoading,
            session,
            user: session?.user ?? null,
            lastError,
            signIn,
            signUp,
            signOut,
        }),
        [isLoading, session, lastError]
    );

    return <ClientAuthContext.Provider value={value}>{children}</ClientAuthContext.Provider>;
}

export function useClientAuth() {
    const context = useContext(ClientAuthContext);
    if (!context) {
        throw new Error("useClientAuth, ClientAuthProvider bileşeni içinde kullanılmalıdır.");
    }
    return context;
}
